import {
  Box,
  Card,
  CardActionArea,
  Chip,
  ChipProps,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Icons } from 'utoniq-ui'

import {
  EventPreSaleBoxFragment,
  PreSaleStatus,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { DateTime } from '@/core/entities'

type PreSaleCardProps = {
  preSale: EventPreSaleBoxFragment
}

export const PreSaleCard = ({ preSale }: PreSaleCardProps) => {
  const { t } = useTranslation('eventDetail')
  const { generatePath, routerPages } = useRouter()

  // 応募期間・購入期間の表示
  const applicationPeriod = useMemo(() => {
    if (preSale.type === 'lottery') {
      return {
        label: t('applicationPeriod'),
        value: `${DateTime.formatDate(
          preSale.lottery?.applicationStartedAt,
          'YYYY/MM/DD(ddd) HH:mm'
        )} 〜 ${DateTime.formatDate(
          preSale.lottery?.applicationEndedAt,
          'YYYY/MM/DD(ddd) HH:mm'
        )}`,
      }
    }
    return {
      label: t('purchasablePeriod'),
      value: `${DateTime.formatDate(
        preSale.firstComeFirstServed?.purchasableStartedAt,
        'YYYY/MM/DD(ddd) HH:mm'
      )} 〜 ${DateTime.formatDate(
        preSale.firstComeFirstServed?.purchasableEndedAt,
        'YYYY/MM/DD(ddd) HH:mm'
      )}`,
    }
  }, [preSale])

  const resaleAvailable = useMemo(
    () =>
      preSale.type === PreSaleType.FirstComeFirstServed &&
      preSale.items?.some(({ isResaleAvailable }) => isResaleAvailable),
    [preSale.items, preSale.type]
  )

  const { color } = useMemo<{ color: ChipProps['color'] }>(() => {
    switch (preSale.preSaleStatus) {
      case PreSaleStatus.Accepting:
        return { color: 'primary' }
      case PreSaleStatus.BeforeReceiption:
        return { color: 'secondary' }
      case PreSaleStatus.SoldOut:
        return { color: 'error' }
      default:
        return { color: 'default' }
    }
  }, [preSale.preSaleStatus])

  return (
    <Card>
      <CardActionArea
        href={
          preSale.type === 'lottery'
            ? generatePath(routerPages.LotteryDetail, {
                preSaleId: preSale.id,
              })
            : generatePath(routerPages.FirstComeFirstServedDetail, {
                preSaleId: preSale.id,
              })
        }
      >
        <Stack>
          <Stack sx={{ bgcolor: 'grey.50', p: 5, pb: 3 }}>
            {preSale.isPremium && (
              <Stack
                direction="row"
                spacing={0}
                sx={{ placeItems: 'center', pb: 2 }}
              >
                <Icons.MembersOnly fontSize="small" color="primary" />
                <Typography variant="subtitle2" color="text.hint">
                  {t('isPremium')}
                </Typography>
              </Stack>
            )}
            <Typography variant="subtitle1">{preSale.title}</Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    '.MuiChip-root': {
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'grey.500',
                      display: 'grid',
                      placeItems: 'center',
                    },
                  }}
                >
                  <Chip size="small" label={t(preSale?.type)} />
                </Box>
                <Chip
                  size="small"
                  sx={
                    // Chipに設定されていないカラーなので、独自に設定する
                    preSale.preSaleStatus === 'acceptingLottery'
                      ? {
                          bgcolor: 'info.light',
                          color: 'default',
                          fontSize: '10px',
                          fontWeight: 700,
                        }
                      : preSale.preSaleStatus === 'purchasable'
                      ? {
                          bgcolor: 'secondary.light',
                          color: 'default',
                          fontSize: '10px',
                          fontWeight: 700,
                        }
                      : undefined
                  }
                  label={t(`${preSale.preSaleStatus}`)}
                  color={color}
                />
              </Stack>
              {resaleAvailable && (
                <Typography variant="subtitle1" color="primary.main">
                  {t('resaleAvailable')}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Box sx={{ bgcolor: 'grey.100', px: 5, py: 3 }}>
            <Typography variant="caption" sx={{ fontSize: '10px' }}>
              {applicationPeriod.label}
              {applicationPeriod.value}
            </Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  )
}
